import { Component, Input, Output, EventEmitter, ViewEncapsulation, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButton } from "@angular/material/button";
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface NetworkDetails {
  container_ip?: string;
  router_ip_target?: string;
  timestamp?: string;
  status?: string;
}

@Component({
  selector: 'app-host-watcher',
  standalone: true,
  imports: [CommonModule, MatButton],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div *ngIf="show" class="modal-backdrop" (click)="onBackdropClick($event)">
      <div class="modal-content" (click)="$event.stopPropagation()" [ngClass]="getModalClass()">
        <div class="modal-header">
          <h2 [ngClass]="getHeaderClass()">
            <span class="status-icon">{{ getStatusIcon() }}</span>
            {{ title }}
          </h2>
          <button class="close-btn" (click)="close()" aria-label="Close">×</button>
        </div>
        <div class="modal-body">
          <div class="main-message">
            <p [innerHTML]="safeMessage"></p>
          </div>

          <div *ngIf="networkDetails && shouldShowDetails()" class="network-details">
            <h4>Network Information</h4>
            <div class="detail-grid">
              <div class="detail-item" *ngIf="networkDetails.container_ip">
                <span class="label">Container IP:</span>
                <span class="value">{{ networkDetails.container_ip }}</span>
              </div>
              <div class="detail-item" *ngIf="networkDetails.router_ip_target">
                <span class="label">Router IP:</span>
                <span class="value">{{ networkDetails.router_ip_target }}</span>
              </div>
              <div class="detail-item" *ngIf="networkDetails.timestamp">
                <span class="label">Last Check:</span>
                <span class="value">{{ networkDetails.timestamp }}</span>
              </div>
              <div class="detail-item" *ngIf="networkDetails.status">
                <span class="label">Status Code:</span>
                <span class="value status-code">{{ networkDetails.status }}</span>
              </div>
            </div>
          </div>

          <div *ngIf="getActionSuggestions()" class="action-suggestions">
            <h4>Suggested Actions</h4>
            <ul>
              <li *ngFor="let suggestion of getActionSuggestions()">{{ suggestion }}</li>
            </ul>
          </div>
        </div>
        <div class="modal-footer">
          <button mat-stroked-button color='warn' (click)="close()">Close</button>
          <button *ngIf="shouldShowRetryButton()" mat-raised-button color='accent' (click)="onRetry()">
            Retry Check
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      backdrop-filter: blur(2px);
      animation: fadeIn 0.2s ease-out;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-30px) scale(0.9);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .modal-content {
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
      min-width: 380px;
      max-width: 80vw !important;
      width: 90%;
      max-height: 80vh;
      animation: slideIn 0.3s ease-out;
    }

    .modal-success {
      border-left: 4px solid #059669;
    }

    .modal-warning {
      border-left: 4px solid #d97706;
    }

    .modal-error {
      border-left: 4px solid #dc2626;
    }

    .modal-info {
      border-left: 4px solid #3b82f6;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px 16px;
      border-bottom: 1px solid #e5e7eb;
      background: #f8fafc;
    }

    .modal-header h2 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .status-icon {
      font-size: 1.5rem;
    }

    .header-fully-connected {
      color: #059669;
    }

    .header-no-internet {
      color: #d97706;
    }

    .header-no-lan-connection, .header-service-down {
      color: #dc2626;
    }

    .header-unknown, .header-error {
      color: #6b7280;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 24px !important;
      cursor: pointer;
      color: #6b7280;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s;
    }

    .close-btn:hover {
      background: #e5e7eb;
      color: #374151;
    }

    .modal-body {
      padding: 20px 24px;
    }

    .main-message p {
      margin: 0 0 16px 0;
      color: #4b5563;
      line-height: 1.6;
      /* removed white-space: pre-line because we are now using <br> tags manually */
    }

    .network-details {
      margin-top: 20px;
      padding: 16px;
      background: #f8fafc;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
    }

    .network-details h4 {
      margin: 0 0 12px 0;
      color: #374151;
      font-size: 1rem;
      font-weight: 600;
    }

    .detail-grid {
      display: grid;
      gap: 8px;
    }

    .detail-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 0;
    }

    .label {
      font-weight: 500;
      color: #6b7280;
      font-size: 0.875rem;
    }

    .value {
      color: #374151;
      font-size: 0.875rem;
      font-family: monospace;
    }

    .status-code {
      background: #e5e7eb;
      padding: 2px 6px;
      border-radius: 4px;
      font-weight: 500;
    }

    .action-suggestions {
      margin-top: 20px;
      padding: 16px;
      background: #fef3c7;
      border-radius: 8px;
      border-left: 4px solid #f59e0b;
    }

    .action-suggestions h4 {
      margin: 0 0 12px 0;
      color: #92400e;
      font-size: 1rem;
      font-weight: 600;
    }

    .action-suggestions ul {
      margin: 0;
      padding-left: 20px;
      color: #92400e;
    }

    .action-suggestions li {
      margin-bottom: 6px;
      line-height: 1.5;
    }

    .modal-footer {
      padding: 16px 24px 20px;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      border-top: 1px solid #e5e7eb;
      background: #f8fafc;
    }

    .btn {
      padding: 8px 16px;
      border-radius: 6px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
      font-size: 14px;
    }

    .btn-primary {
      background: #3b82f6;
      color: white;
    }

    .btn-primary:hover {
      background: #2563eb;
    }

    .btn-secondary {
      background: #6b7280;
      color: white;
    }

    .btn-secondary:hover {
      background: #4b5563;
    }

    /* Responsive design */
    @media (max-width: 640px) {
      .modal-content {
        margin: 16px;
        width: calc(100% - 32px);
        min-width: 300px;
      }

      .modal-header,
      .modal-body,
      .modal-footer {
        padding-left: 20px;
        padding-right: 20px;
      }

      .modal-footer {
        flex-direction: column-reverse;
      }

      .btn {
        width: 100%;
      }
    }
  `]
})
export class HostWatcherComponent implements OnChanges {
  @Input() show = false;
  @Input() title = 'Network Status';
  @Input() message = '';
  @Input() status = 'unknown';
  @Input() networkDetails: NetworkDetails = {};
  @Output() closeModal = new EventEmitter<void>();
  @Output() retryCheck = new EventEmitter<void>();

  safeMessage: SafeHtml = '';

  constructor(private sanitizer: DomSanitizer) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['message']) {
      this.safeMessage = this.sanitizer.bypassSecurityTrustHtml(this.message);
    }
  }

  close() {
    this.show = false;
    this.closeModal.emit();
  }

  onBackdropClick(event: MouseEvent) {
    this.close();
  }

  onRetry() {
    this.retryCheck.emit();
  }

  getModalClass(): string {
    switch (this.status) {
      case 'fully_connected':
        return 'modal-success';
      case 'no_internet':
        return 'modal-warning';
      case 'no_lan_connection':
      case 'service_down':
        return 'modal-error';
      default:
        return 'modal-info';
    }
  }

  getHeaderClass(): string {
    return `header-${this.status.replace('_', '-')}`;
  }

  getStatusIcon(): string {
    switch (this.status) {
      case 'fully_connected':
        return '✅';
      case 'no_internet':
        return '⚠️';
      case 'no_lan_connection':
      case 'service_down':
        return '❌';
      case 'unknown':
        return '❓';
      default:
        return 'ℹ️';
    }
  }

  shouldShowDetails(): boolean {
    return this.networkDetails && Object.keys(this.networkDetails).length > 1;
  }

  shouldShowRetryButton(): boolean {
    return ['no_internet', 'no_lan_connection', 'service_down', 'error'].includes(this.status);
  }

  getActionSuggestions(): string[] | null {
    switch (this.status) {
      case 'no_internet':
        return [
          'Check if your internet service provider is experiencing outages',
          'Restart your modem/router',
          'Contact your network administrator',
          'Verify DNS settings'
        ];

      case 'no_lan_connection':
        return [
          'Check all network cables are properly connected',
          'Restart your router/switch',
          'Verify the router is powered on',
          'Check if other devices can connect to the network'
        ];

      case 'service_down':
        return [
          'Verify the host watcher service is running',
          'Check if the monitoring container is healthy',
          'Restart the monitoring service',
          'Check container logs for errors'
        ];

      case 'unknown':
        return [
          'Check the HOST_GATEWAY_IP configuration',
          'Verify network monitoring settings',
          'Contact system administrator'
        ];

      default:
        return null;
    }
  }
}